import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  MagnifyingGlassIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  PlayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { applications, applicationCategories, difficultyLevels, Application } from '../data/applications';

interface ApplicationsPageProps {
  onBack: () => void;
}

const ApplicationsPage: React.FC<ApplicationsPageProps> = ({ onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedDifficulty, setSelectedDifficulty] = useState('All');
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);

  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || app.category === selectedCategory;
    const matchesDifficulty = selectedDifficulty === 'All' || app.difficulty === selectedDifficulty;
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (selectedApplication) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <button
              onClick={() => setSelectedApplication(null)}
              className="flex items-center text-gray-600 hover:text-primary-600 transition-colors mb-4"
            >
              ← Back to Applications
            </button>
            
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{selectedApplication.title}</h1>
                <p className="text-lg text-gray-600 mt-2">{selectedApplication.description}</p>
              </div>
              
              <div className="flex items-center gap-4 mt-4 lg:mt-0">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(selectedApplication.difficulty)}`}>
                  {selectedApplication.difficulty}
                </span>
                <div className="flex items-center text-gray-600">
                  <ClockIcon className="w-5 h-5 mr-2" />
                  {selectedApplication.estimatedTime}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Problem & Solution */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2" />
                      The Problem
                    </h3>
                    <p className="text-gray-600">{selectedApplication.problem}</p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                      Our Solution
                    </h3>
                    <p className="text-gray-600">{selectedApplication.solution}</p>
                  </div>
                </div>
              </div>

              {/* Steps */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Step-by-Step Instructions</h3>
                <div className="space-y-6">
                  {selectedApplication.steps.map((step, index) => (
                    <motion.div
                      key={index}
                      className="flex items-start"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                        <span className="text-primary-600 font-semibold text-sm">
                          {step.step}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                          {step.title}
                        </h4>
                        <p className="text-gray-600 mb-3">{step.description}</p>
                        {step.tips && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-blue-800 text-sm">
                              <strong>Pro Tip:</strong> {step.tips}
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Tools & Materials */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Tools & Materials</h3>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <WrenchScrewdriverIcon className="w-5 h-5 mr-2" />
                    Tools Needed
                  </h4>
                  <ul className="space-y-1">
                    {selectedApplication.tools.map((tool, index) => (
                      <li key={index} className="text-sm text-gray-600">• {tool}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Materials Required</h4>
                  <ul className="space-y-1">
                    {selectedApplication.materials.map((material, index) => (
                      <li key={index} className="text-sm text-gray-600">• {material}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Safety Notes */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-red-900 mb-4">Safety Notes</h3>
                <ul className="space-y-2">
                  {selectedApplication.safetyNotes.map((note, index) => (
                    <li key={index} className="text-sm text-red-800 flex items-start">
                      <span className="text-red-500 mr-2">⚠️</span>
                      {note}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Video */}
              {selectedApplication.videoUrl && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Video Tutorial</h3>
                  <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <PlayIcon className="w-16 h-16 text-primary-600 mx-auto mb-2" />
                      <p className="text-gray-600">Watch Video Tutorial</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Before/After */}
              {(selectedApplication.beforeImage || selectedApplication.afterImage) && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Before & After</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedApplication.beforeImage && (
                      <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                        <span className="text-gray-500 text-sm">Before</span>
                      </div>
                    )}
                    {selectedApplication.afterImage && (
                      <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                        <span className="text-gray-500 text-sm">After</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Applications Library</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive guides for common household problems and professional solutions. 
              Learn step-by-step techniques used by industry experts.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex gap-2">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {applicationCategories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {difficultyLevels.map(level => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Applications Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredApplications.map((application, index) => (
            <motion.div
              key={application.id}
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              onClick={() => setSelectedApplication(application)}
            >
              {/* Application Image */}
              <div className="aspect-video bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-3 bg-white rounded-lg shadow-md flex items-center justify-center">
                    <span className="text-2xl">
                      {application.category === 'Roofing' && '🏠'}
                      {application.category === 'Windows' && '🪟'}
                      {application.category === 'Foundation' && '🏗️'}
                      {application.category === 'Painting' && '🎨'}
                      {application.category === 'Gutters' && '🌧️'}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">{application.title}</h3>
                </div>
              </div>

              {/* Application Info */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-primary-600">{application.category}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(application.difficulty)}`}>
                    {application.difficulty}
                  </span>
                </div>

                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {application.description}
                </p>

                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center">
                    <ClockIcon className="w-4 h-4 mr-1" />
                    {application.estimatedTime}
                  </div>
                  <div className="flex items-center">
                    <WrenchScrewdriverIcon className="w-4 h-4 mr-1" />
                    {application.tools.length} tools
                  </div>
                </div>

                <button className="w-full btn-primary">
                  View Application
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* No Results */}
        {filteredApplications.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <MagnifyingGlassIcon className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No applications found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
          </div>
        )}

        {/* Features Section */}
        <div className="mt-16 bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Why Use Our Applications Library?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">👨‍🔧</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Professional Techniques</h3>
              <p className="text-gray-600 text-sm">
                Learn the same techniques used by industry professionals with detailed step-by-step instructions.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🛡️</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Safety First</h3>
              <p className="text-gray-600 text-sm">
                Every application includes comprehensive safety notes and best practices to keep you protected.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💰</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Cost Effective</h3>
              <p className="text-gray-600 text-sm">
                Save money by learning to handle common household issues yourself with professional guidance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationsPage;
